import { Alarm } from "@mui/icons-material";
import PercentIcon from "@mui/icons-material/Percent";
import { Box } from "@mui/material";
import { ProjectType } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import {
	BoundAsyncSelect,
	BoundDateField,
	BoundNumberField,
	BoundTextField,
	useAsyncSelectAccountingProfileQuery,
} from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React from "react";

import type { ProjectEditProps } from "./ProjectEditProps";
import type { Project } from "../types/ProjectTypes";

export const AccountingInfoProject = (props: ProjectEditProps) => {
	const { t } = useTranslation();

	const { project, setProject, errors, setErrors } = props;

	const useBinding = <TValue,>(selector: (m: Project) => TValue) => {
		return useBoundDeep(project, setProject, selector, errors, setErrors);
	};

	const projectAccountingProfileField = useBinding(
		(p) => p.accountingInformation.accountingProfile,
	);
	const searchAccountingProfileHook = useAsyncSelectAccountingProfileQuery();

	const projectProvisionField = useBinding((p) => p.accountingInformation.provision);
	const projectProvisionDescriptionField = useBinding(
		(p) => p.accountingInformation.provisionDescription,
	);
	const projectTimeTrackingResolutionField = useBinding(
		(p) => p.accountingInformation.timeTrackingResolution,
	);
	const projectClientContractNumberField = useBinding(
		(p) => p.accountingInformation.clientContractNumber,
	);
	const projectClientContractDateField = useBinding(
		(p) => p.accountingInformation.clientContractDate,
	);
	const projectClientOrderingNumberField = useBinding(
		(p) => p.accountingInformation.clientOrderingNumber,
	);
	const projectClientOrderingDateField = useBinding(
		(p) => p.accountingInformation.clientOrderingDate,
	);
	const projectClientProjectNumberField = useBinding(
		(p) => p.accountingInformation.clientProjectNumber,
	);
	const projectSupplierProjectNumberField = useBinding(
		(p) => p.accountingInformation.supplierProjectNumber,
	);

	return (
		<div>
			<div className="hrTitle lightGreyFont paddingBottom40">
				<span className="left">{t("billing").toLocaleUpperCase()}</span>
				<hr className="right" />
			</div>
			<Box className="fullCenter w-100" sx={{ mt: 2.5 }}>
				<BoundAsyncSelect
					label={t("billing-profile")}
					boundedValue={projectAccountingProfileField}
					searchHook={searchAccountingProfileHook}
					additionalProps={{ className: "w-100 left" }}
				/>
			</Box>
			<Box className="fullCenter w-100" sx={{ mt: 2.5 }}>
				<BoundNumberField
					label={t("provision")}
					boundedValue={projectProvisionField}
					additionalProps={{ className: "w-100 left" }}
					icon={<PercentIcon />}
					iconBackgroundColor="#1a455c"
				/>
			</Box>
			<Box className="fullCenter w-100" sx={{ mt: 2.5 }}>
				<BoundTextField
					label={t("provision-description")}
					boundedValue={projectProvisionDescriptionField}
					additionalProps={{ className: "w-100 left", rows: 2, multiline: true }}
				/>
			</Box>
			<Box className="fullCenter w-100" sx={{ mt: 2.5 }}>
				<BoundNumberField
					label={t("time-tracking-resolution")}
					boundedValue={projectTimeTrackingResolutionField}
					additionalProps={{ className: "w-100 left" }}
					icon={<Alarm />}
					iconBackgroundColor="#1a455c"
				/>
			</Box>

			{project.projectType !== ProjectType.Internal && (
				<>
					<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
						<span className="left">{t("client-infos").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter w-100" sx={{ mt: 2.5, display: "flex", gap: "2%" }}>
						<BoundTextField
							label={t("contract-number")}
							boundedValue={projectClientContractNumberField}
							additionalProps={{
								sx: { width: "60%" },
								variant: "outlined",
							}}
						/>
						<BoundDateField
							label={t("date-of-contract")}
							boundedValue={projectClientContractDateField}
							additionalProps={{
								sx: { width: "40%" },
							}}
						/>
					</Box>
					<Box className="fullCenter w-100" sx={{ mt: 2.5, display: "flex", gap: "2%" }}>
						<BoundTextField
							label={t("ordering-number")}
							boundedValue={projectClientOrderingNumberField}
							additionalProps={{
								sx: { width: "60%" },
								variant: "outlined",
							}}
						/>
						<BoundDateField
							label={t("date-of-ordering")}
							boundedValue={projectClientOrderingDateField}
							additionalProps={{
								sx: { width: "40%" },
							}}
						/>
					</Box>
					<Box className="fullCenter w-100" sx={{ mt: 2.5 }}>
						<BoundTextField
							label={t("project-number")}
							boundedValue={projectClientProjectNumberField}
							additionalProps={{
								sx: { width: "100%" },
								variant: "outlined",
							}}
						/>
					</Box>
				</>
			)}

			<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
				<span className="left">{t("supplier-information").toLocaleUpperCase()}</span>
				<hr className="right" />
			</div>
			<Box className="fullCenter w-100 paddingBottom40" sx={{ mt: 2.5 }}>
				<BoundTextField
					label={t("project-number")}
					boundedValue={projectSupplierProjectNumberField}
					additionalProps={{
						sx: { width: "100%" },
						variant: "outlined",
					}}
				/>
			</Box>
		</div>
	);
};

AccountingInfoProject.displayName = "AccountingInfoProject";
